import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

import withLazyLoading from '../../hoc/withLazyLoading';
import { ACCOUNT_ROUTES, CTL_PATTERNS, CTL_ROUTES, USER_CENTER_ROUTES } from '../../navigation/paths';
import { createCrumbHandle, withCrumbParents } from '../../utilities/auth';
import { EVERYONE, PEOPLE_RULES, access, param } from '../../access';

import { guardedRoute } from './guardedRoute';

const FEATURE = 'close-the-loop';

const NotificationSettingsScreen = withLazyLoading(() =>
  import('../../modules/close-the-loop/components/settings/NotificationSettingsScreen').then((m) => ({
    default: m.NotificationSettingsScreen,
  })),
);

const QuietHoursScreen = withLazyLoading(() =>
  import('../../modules/close-the-loop/components/quiet-hours/QuietHoursScreen').then((m) => ({
    default: m.QuietHoursScreen,
  })),
);

const RecentAlertsScreen = withLazyLoading(() =>
  import('../../modules/close-the-loop/components/recent/RecentAlertsScreen').then((m) => ({
    default: m.RecentAlertsScreen,
  })),
);

const SiteSettingsRoute = withLazyLoading(() =>
  import('../../modules/close-the-loop/components/site/SiteSettingsRoute').then((m) => ({
    default: m.SiteSettingsRoute,
  })),
);

const GuardedSiteSettings: React.FC = () => {
  const { plantId } = useParams();
  return guardedRoute(FEATURE, <SiteSettingsRoute />, [plantId]);
};

const ToSiteSettings: React.FC = () => {
  const { plantId } = useParams();
  const { state } = useLocation();
  if (!plantId) return null;
  return <Navigate to={CTL_ROUTES.siteSettings(plantId)} state={state} replace />;
};

const PROFILE_CRUMB = { label: 'Profile', pathname: ACCOUNT_ROUTES.contact };
const NOTIFICATIONS_CRUMB = { label: 'Notifications', pathname: CTL_ROUTES.settings };
const PLANTS_CRUMB = { label: 'Plants & modules', pathname: USER_CENTER_ROUTES.plants };

const closeTheLoopRoutes = [
  {
    path: CTL_ROUTES.settings,
    element: guardedRoute(FEATURE, <NotificationSettingsScreen />),
    handle: { ...withCrumbParents(createCrumbHandle('Notifications'), [PROFILE_CRUMB]), ...access(EVERYONE) },
  },
  {
    path: CTL_ROUTES.quietHours,
    element: guardedRoute(FEATURE, <QuietHoursScreen />),
    handle: { ...withCrumbParents(createCrumbHandle('Quiet hours'), [PROFILE_CRUMB, NOTIFICATIONS_CRUMB]), ...access(EVERYONE) },
  },
  {
    path: CTL_ROUTES.recent,
    element: guardedRoute(FEATURE, <RecentAlertsScreen />),
    handle: { ...withCrumbParents(createCrumbHandle('Recent alerts'), [PROFILE_CRUMB, NOTIFICATIONS_CRUMB]), ...access(EVERYONE) },
  },
  {
    path: CTL_PATTERNS.siteSettings,
    element: <GuardedSiteSettings />,
    handle: { ...withCrumbParents(createCrumbHandle('Site settings'), [PLANTS_CRUMB]), ...access(PEOPLE_RULES.siteSettings, param('plantId')) },
  },
  {
    path: CTL_PATTERNS.plantPolicy,
    element: <ToSiteSettings />,
    handle: access(PEOPLE_RULES.siteSettings, param('plantId')),
  },
  {
    path: CTL_PATTERNS.plantRouting,
    element: <ToSiteSettings />,
    handle: access(PEOPLE_RULES.siteSettings, param('plantId')),
  },
];

export default closeTheLoopRoutes;
